import React, { useEffect, useState } from 'react';
import {
  Alert,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Space,
  Typography,
  message,
} from 'antd';
import { OrderDto, useRefundOrder } from '@shared/api/hooks/useOrders';

interface Props {
  order: OrderDto | null;
  open: boolean;
  onClose: () => void;
}

type RefundMode = 'full' | 'partial';

export function RefundModal({ order, open, onClose }: Props): React.ReactElement {
  const [form] = Form.useForm();
  const refundMutation = useRefundOrder();
  const [mode, setMode] = useState<RefundMode>('full');

  const alreadyRefunded = Number(order?.refundedAmount ?? 0);
  const remaining = Number(((order?.finalAmount ?? 0) - alreadyRefunded).toFixed(2));

  useEffect(() => {
    if (open) {
      form.resetFields();
      setMode('full');
    }
  }, [open, form]);

  if (!order) return <span />;

  const onSubmit = async (): Promise<void> => {
    try {
      const values = (await form.validateFields()) as {
        amount?: number;
        reason: string;
        restock: boolean;
      };
      const amount = mode === 'partial' ? values.amount : undefined;
      const res = await refundMutation.mutateAsync({
        id: order.id,
        dto: {
          amount,
          reason: values.reason,
          restock: values.restock,
        },
      });
      message.success(`Rimborso ${(res.amountCents / 100).toFixed(2)} ${order.currency} avviato`);
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      if (msg) message.error(Array.isArray(msg) ? msg.join('; ') : msg);
    }
  };

  const cannotRefund = !order.paymentIntentId || order.paymentStatus === 'REFUNDED';

  return (
    <Modal
      open={open}
      title={`Rimborso ordine ${order.pickupQrCode}`}
      okText="Conferma rimborso"
      okButtonProps={{
        danger: true,
        loading: refundMutation.isPending,
        disabled: cannotRefund,
      }}
      cancelText="Annulla"
      onCancel={onClose}
      onOk={onSubmit}
      destroyOnClose
    >
      {cannotRefund && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            order.paymentStatus === 'REFUNDED'
              ? 'Ordine già completamente rimborsato.'
              : 'Nessun PaymentIntent Stripe — ordine cash-on-pickup non rimborsabile via Stripe.'
          }
        />
      )}

      <Space direction="vertical" size={2} style={{ marginBottom: 12 }}>
        <Typography.Text>
          Totale: <b>{order.finalAmount.toFixed(2)} {order.currency}</b>
        </Typography.Text>
        {alreadyRefunded > 0 && (
          <Typography.Text type="secondary">
            Già rimborsato: {alreadyRefunded.toFixed(2)} {order.currency}
          </Typography.Text>
        )}
        <Typography.Text>
          Disponibile per rimborso: <b>{remaining.toFixed(2)} {order.currency}</b>
        </Typography.Text>
      </Space>

      <Form
        form={form}
        layout="vertical"
        disabled={cannotRefund}
        initialValues={{ restock: true, reason: '' }}
      >
        <Form.Item label="Tipo di rimborso">
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value as RefundMode)}>
            <Radio value="full">Rimborso totale ({remaining.toFixed(2)})</Radio>
            <Radio value="partial">Parziale</Radio>
          </Radio.Group>
        </Form.Item>

        {mode === 'partial' && (
          <Form.Item
            label="Importo da rimborsare"
            name="amount"
            rules={[
              { required: true, message: 'Inserisci importo' },
              {
                validator: async (_, v: number) => {
                  if (!v) return;
                  if (v <= 0 || v > remaining) {
                    throw new Error(`Tra 0.01 e ${remaining.toFixed(2)}`);
                  }
                },
              },
            ]}
          >
            <InputNumber
              min={0.01}
              max={remaining}
              step={0.01}
              precision={2}
              addonAfter={order.currency}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        <Form.Item
          label="Motivo"
          name="reason"
          rules={[
            { required: true, message: 'Motivo obbligatorio' },
            { min: 10, message: 'Almeno 10 caratteri' },
            { max: 500 },
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Es. Cliente ha chiesto reso per taglia errata"
          />
        </Form.Item>

        <Form.Item name="restock" valuePropName="checked">
          <Checkbox>
            Reintegra magazzino (solo se ordine non spedito/ritirato)
          </Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}
